import React from "react";
import { Crown, Handshake, Users, MessageCircle, FileSignature, ShieldCheck } from "lucide-react";

export default function PowerPositioningPage() {
  return (
    <div data-testid="view-power" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Manifeste" title="Dans ce business, le plus technique perd" index="01">
        <p className="italic text-[#D4AF37] text-lg border-l-2 border-[#D4AF37] pl-5">
          Le mieux positionné gagne.
        </p>
        <p>
          La connaissance des Incoterms, des procédures SWIFT, des types de LC — tout ça est nécessaire mais pas
          suffisant. Des milliers de personnes connaissent les procédures. Ce qui est rare, c'est le
          <strong className="text-white"> positionnement</strong>. L'accès. La légitimité perçue.
        </p>
      </Section>

      <Section eyebrow="Mandat" title="Devenir mandaté — et pas juste middleman inutile" index="02">
        <p>
          Différence fondamentale : un <em className="text-white">intermédiaire</em> est une personne entre deux parties.
          Un <strong className="text-white">mandataire</strong> est une personne <strong className="text-white">autorisée
          à agir</strong> au nom d'une partie. L'un est ignoré. L'autre est respecté et payé.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          <RouteCard
            route="01"
            name="Relation directe"
            body="Tu construis une relation réelle avec un producteur, un trader, un fonds. Avec le temps et après avoir prouvé ta valeur (amené un acheteur qualifié, facilité une communication, résolu un problème), ils te donnent un mandat formel pour une zone géographique ou un type d'acheteur."
          />
          <RouteCard
            route="02"
            name="Structure"
            body="Tu crées ou rejoins une structure (société de trading, family office, cabinet de conseil) qui a déjà des mandats ou peut en négocier. Le mandat devient institutionnel — plus solide."
          />
          <RouteCard
            route="03"
            name="Expertise démontrée"
            body="Tu développes une expertise reconnue sur un produit ou une région. Tu publies, tu interviens dans des events, tu es cité. Sellers et acheteurs viennent à toi — le mandat devient la conséquence logique de ta réputation."
          />
        </div>

        <Callout icon={FileSignature} title="Ce qu'un mandat doit contenir pour être valide">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Identité complète du mandant (entité légale, n° d'enregistrement, dirigeant signataire)</li>
            <li>Identité du mandataire</li>
            <li>Périmètre précis (produit, zone géographique, durée)</li>
            <li>Commission ou rémunération prévue</li>
            <li>Signature et cachet du mandant</li>
            <li>Idéalement : légalisation apostille ou notarisation selon la juridiction</li>
          </ul>
        </Callout>
      </Section>

      <Section eyebrow="Indispensabilité" title="Se rendre irremplaçable dans une chaîne" index="03">
        <p>
          La question n'est pas "comment entrer dans une chaîne" — n'importe qui peut s'insérer entre deux parties.
          La question est : <strong className="text-white">pourquoi aurait-on besoin que tu restes là ?</strong>
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Ce qui te rend indispensable</div>
            <div className="font-serif text-xl mt-2 text-white">Les 4 leviers</div>
            <ul className="mt-6 space-y-3 text-sm text-[#E4E4E7]">
              <li><strong className="text-white">Accès exclusif</strong> — tu connais personnellement l'acheteur ou le seller, la relation passe par toi. Si tu disparais, la transaction disparaît.</li>
              <li><strong className="text-white">Confiance transversale</strong> — les deux parties te font confiance, mais pas (encore) entre elles. Tu es le pont.</li>
              <li><strong className="text-white">Compétence technique</strong> — tu comprends les aspects légaux, les structures de financement, la compliance que les autres ne maîtrisent pas.</li>
              <li><strong className="text-white">Capacité de résolution</strong> — quand quelque chose bloque (document manquant, banque qui refuse, litige de spec), tu sais débloquer.</li>
            </ul>
          </div>
          <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">Ce qui te rend remplaçable</div>
            <div className="font-serif text-xl mt-2 text-white">Les 3 pièges</div>
            <ul className="mt-6 space-y-3 text-sm text-[#E4E4E7]">
              <li>▸ Juste un "passeur d'info" entre deux personnes qui pourraient se parler directement.</li>
              <li>▸ Aucune relation personnelle ni exclusive avec aucune des deux parties.</li>
              <li>▸ Tu ne comprends pas le deal assez bien pour répondre à des questions techniques.</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="Commission" title="NCNDA, IMFPA : sécuriser sa commission" index="04">
        <p>
          L'une des questions les plus importantes — et les plus négligées. Des gens travaillent des mois sur un deal,
          il se conclut, et <strong className="text-white">ils ne voient jamais leur commission</strong>. Rarement
          mauvaise foi. Souvent, l'absence de cadre légal préétabli.
        </p>

        <div className="mt-10 space-y-6">
          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-7 md:p-8">
            <div className="flex items-center gap-3">
              <FileSignature className="h-5 w-5 text-[#D4AF37]" />
              <span className="font-mono text-[#D4AF37]">NCNDA</span>
              <span className="font-serif text-xl">— Non-Circumvention, Non-Disclosure Agreement</span>
            </div>
            <p className="mt-4 text-sm text-[#A1A1AA]">
              Accord de base. Les signataires ne peuvent pas contourner les intermédiaires pour traiter directement, et
              s'engagent à la confidentialité. À signer <strong className="text-white">avant tout échange</strong>
              d'informations sensibles (noms de sellers/buyers, contacts bancaires).
            </p>
            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Points critiques</div>
              <ul className="space-y-2 text-sm text-[#E4E4E7]">
                <li>▸ Durée (2 à 5 ans)</li>
                <li>▸ Clause de pénalité en cas de circumvention (souvent un multiple de la commission perdue)</li>
                <li>▸ Juridiction applicable : UK, Suisse et Singapour sont les plus solides</li>
                <li>▸ Signataires : entités légales, pas particuliers seuls</li>
              </ul>
            </div>
          </div>

          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-7 md:p-8">
            <div className="flex items-center gap-3">
              <FileSignature className="h-5 w-5 text-[#D4AF37]" />
              <span className="font-mono text-[#D4AF37]">IMFPA</span>
              <span className="font-serif text-xl">— Irrevocable Master Fee Protection Agreement</span>
            </div>
            <p className="mt-4 text-sm text-[#A1A1AA]">
              Document qui formalise la commission de chaque intermédiaire et qui est transmis à la banque réglante au
              moment du closing. Indique qui reçoit quoi, sur quel compte, à quelle date.
              <strong className="text-white"> Irrevocable</strong> — une fois signé par toutes les parties, il ne peut
              plus être annulé unilatéralement.
            </p>
            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Pour être exécutable</div>
              <ul className="space-y-2 text-sm text-[#E4E4E7]">
                <li>▸ Signé par toutes les parties de la chaîne ET accepté par la banque</li>
                <li>▸ Détails bancaires (SWIFT, IBAN, bénéficiaire) exacts</li>
                <li>▸ Référence explicite au contrat de transaction principal</li>
              </ul>
            </div>
          </div>
        </div>

        <Callout icon={ShieldCheck} title="Conseil pratique absolu">
          Ne travaille jamais sur un deal sans un <strong className="text-white">NCNDA signé</strong>. Si une partie
          refuse de signer : soit elle ne comprend pas le standard du secteur, soit elle compte te contourner.
          Dans les deux cas : <strong className="text-white">sors</strong>.
        </Callout>
      </Section>

      <Section eyebrow="Communication" title="Parler aux HNWI, traders et fournisseurs" index="05">
        <p>
          HNWI, traders institutionnels et fournisseurs de commodités évoluent dans des environnements où le
          <strong className="text-white"> temps est l'actif le plus rare</strong>. Leur mode de communication reflète ça.
        </p>

        <div className="mt-10 space-y-6">
          <AudienceCard
            icon={Crown}
            audience="HNWI / Family Office"
            rules={[
              "Ne commence jamais par le deal — commence par la relation. Un HNWI qui ne te connaît pas ne mettra pas 50 M$ parce que tu lui as envoyé un SCO.",
              "Parle outcomes et risques maîtrisés, pas procédures. Il veut savoir ce que ça lui rapporte et ce qui le protège — pas comment marche une LC.",
              "Sois bref. >150 mots sur un premier email, c'est déjà trop. Un A4 maximum pour un executive summary.",
              "Démontre la due diligence en amont. Ne lui apporte pas un deal brut : apporte un deal filtré, vérifié, questions résolues.",
            ]}
          />
          <AudienceCard
            icon={Handshake}
            audience="Trader institutionnel"
            rules={[
              "Il parle données : prix spot, spread, volume, delta vs benchmark (Platts, Argus, LME). Sois précis ou tais-toi.",
              "Il teste ta compétence technique très vite. Si tu ignores ce qu'est un contango ou comment marche un B/L téléchargeable, il te catégorise amateur en 30 secondes.",
              "Propose une valeur ajoutée spécifique : un accès, une information, un réseau — pas juste un deal que tout le monde a vu passer.",
            ]}
          />
          <AudienceCard
            icon={Users}
            audience="Fournisseur"
            rules={[
              "Respecte la hiérarchie. Ne demande pas le CEO au premier contact — passe par les bons canaux.",
              "Démontre ta capacité de closing : « J'ai un acheteur qualifié avec une banque de premier rang prête à émettre une LC » vaut infiniment plus que « J'ai des contacts intéressés ».",
              "Sois constant dans le temps. Les fournisseurs sérieux font confiance aux personnes qui restent présentes, pas à celles qui disparaissent entre les deals.",
            ]}
          />
        </div>
      </Section>
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.12),transparent_60%)]" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Crown className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Stratégie · 02</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        <em className="italic text-[#F5F5DC]">Pouvoir</em> &amp; <span className="text-[#D4AF37]">Positionnement</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Devenir mandataire, se rendre indispensable, sécuriser ses commissions (NCNDA / IMFPA), communiquer comme
        un pro avec HNWI, traders et fournisseurs.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const RouteCard = ({ route, name, body }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-[#D4AF37]/30 transition-colors">
    <div className="font-serif text-2xl text-[#D4AF37]">Voie {route}</div>
    <div className="font-serif text-lg mt-3 text-white">{name}</div>
    <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">{body}</p>
  </div>
);

const AudienceCard = ({ icon: Icon, audience, rules }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-7">
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-[#D4AF37]" />
      <span className="font-serif text-xl">{audience}</span>
    </div>
    <ul className="mt-5 space-y-3 text-sm text-[#E4E4E7]">
      {rules.map((r, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-[#D4AF37] shrink-0">▸</span><span>{r}</span>
        </li>
      ))}
    </ul>
  </div>
);
